import {TFunction} from 'i18next';
import {Ref, useCallback, useMemo, useState} from 'react';
import Select, {SelectInstance, GroupBase, MultiValue, SingleValue, Options} from 'react-select';
import CreatableSelect from 'react-select/creatable';

import {filterDuplicatedCompanyWorkers} from 'shared/helpers/worker';
import {useProjectWorkers} from 'shared/hooks/useProjectWorkers';
import {CompanyWorker} from 'shared/models/worker';

import {createProjectWorkerStyles} from '../styles';

import {CustomMenuList, CustomOption, CustomOptionForMulti, CustomSingleValue} from './components';

export interface ProjectWorkerSelectOption {
  label: string;
  value: CompanyWorker;
  workerData: CompanyWorker;
}

type AsyncProjectWorkerSelectProps = {
  onChange?: (options: ProjectWorkerSelectOption[]) => void;
  onSelectChange?: (value: CompanyWorker | CompanyWorker[] | null) => void;
  placeholder: string;
  loadingPlaceholder: string;
  projectId: string;
  value?: ProjectWorkerSelectOption[] | CompanyWorker | CompanyWorker[];
  isMulti?: boolean;
  isCreatable?: boolean;
  isClearable?: boolean;
  formatCreateLabel?: (inputValue: string) => string;
  isValidNewOption?: (inputValue: string) => boolean;
  onCreateOption?: (inputValue: string) => void;
  closeMenuOnSelect?: boolean;
  controlShouldRenderValue?: boolean;
  className?: string;
  id?: string;
  withInviteButton?: boolean;
  selectRef?: Ref<SelectInstance<ProjectWorkerSelectOption, boolean>>;
  taskId?: string;
  t?: TFunction;
  menuIsOpen?: boolean;
  onMenuClose?: () => void;
  [key: string]: any;
};

const isCompanyWorker = (value: any): value is CompanyWorker => {
  return value && 'workerFull' in value;
};

const isCompanyWorkerArray = (value: any): value is CompanyWorker[] => {
  return Array.isArray(value) && value.length > 0 && isCompanyWorker(value[0]);
};

const AsyncProjectWorkerSelect = ({
  onChange,
  onSelectChange,
  placeholder,
  loadingPlaceholder,
  value,
  projectId,
  selectRef,
  withInviteButton = false,
  isCreatable = false,
  isMulti = false,
  ...props
}: AsyncProjectWorkerSelectProps) => {
  const initialValue = useMemo(() => {
    if (!value) return [];

    if (isCompanyWorker(value) || isCompanyWorkerArray(value)) {
      const workers = Array.isArray(value) ? value : [value];
      return workers.map((worker) => ({
        label: worker.workerFull.fullName || worker.workerFull.email || worker.workerFull.mobileNumber,
        value: worker,
        workerData: worker,
      }));
    }

    // Value is already ProjectWorkerSelectOption[]
    return value as ProjectWorkerSelectOption[];
  }, [value]);

  const [selected, setSelected] = useState<ProjectWorkerSelectOption[]>(initialValue);
  const {projectWorkers, isLoading} = useProjectWorkers(projectId);

  const options: Options<ProjectWorkerSelectOption> = useMemo(() => {
    if (projectWorkers) {
      return filterDuplicatedCompanyWorkers(projectWorkers).map((worker) => ({
        label: worker.workerFull.fullName || worker.workerFull.email || worker.workerFull.mobileNumber,
        value: worker,
        workerData: worker,
      }));
    }
    return [];
  }, [projectWorkers]);

  const handleChange = (newValue: SingleValue<ProjectWorkerSelectOption> | MultiValue<ProjectWorkerSelectOption>) => {
    if (isMulti) {
      const multiValue = newValue as MultiValue<ProjectWorkerSelectOption>;
      const normalizedOptions = Array.from(multiValue);
      setSelected(normalizedOptions);
      onChange?.(normalizedOptions);
      onSelectChange?.(normalizedOptions.map((opt) => opt.value));
    } else {
      const singleValue = newValue as SingleValue<ProjectWorkerSelectOption>;
      const normalizedOptions = singleValue ? [singleValue] : [];
      setSelected(normalizedOptions);
      onChange?.(normalizedOptions);
      onSelectChange?.(singleValue?.value || null);
    }
  };

  const getCustomComponents = useCallback(() => {
    const components = {
      Option: isMulti ? CustomOptionForMulti : CustomOption,
      SingleValue: CustomSingleValue,
    };
    if (withInviteButton) {
      Object.assign(components, {MenuList: CustomMenuList});
    }
    return components;
  }, [isMulti, withInviteButton]);

  const baseProps = {
    ref: selectRef,
    defaultInputValue: '',
    isSearchable: true,
    isDisabled: isLoading,
    isLoading,
    menuPlacement: 'auto' as const,
    options,
    className: `react-select override-menu-height ${props.className || ''}`,
    styles: createProjectWorkerStyles(),
    classNamePrefix: 'react-select',
    onChange: handleChange,
    components: getCustomComponents(),
    isMulti,
    value: value ? initialValue : selected,
    closeMenuOnSelect: !isMulti,
    placeholder,
    loadingMessage: () => loadingPlaceholder,
    ...props,
  };

  const SelectComponent = isCreatable ? CreatableSelect : Select;
  return <SelectComponent<ProjectWorkerSelectOption, boolean, GroupBase<ProjectWorkerSelectOption>> {...baseProps} />;
};

export default AsyncProjectWorkerSelect;
